import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { generateWorkoutAdjustments } from '../../../services/anthropicService';
import { userProfileService, workoutPlanService } from '../../../services/supabaseService';
import { useAuth } from '../../../contexts/AuthContext';

const AIWorkoutRecommendations = ({ currentWorkoutPlan, onClose }) => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [clientPerformanceData, setClientPerformanceData] = useState(null);

  useEffect(() => {
    loadClients();
  }, [user]);

  const loadClients = async () => {
    try {
      const { data } = await userProfileService?.getTrainerClients(user?.id);
      setClients(data || []);
    } catch (err) {
      console.error('Error loading clients:', err);
    }
  };

  const loadClientPerformanceData = async (clientId) => {
    try {
      const { data: workouts } = await workoutPlanService?.getAssignedWorkouts(user?.id, 'personal_trainer');
      const clientWorkouts = workouts?.filter(w => w?.clientId === clientId) || [];
      
      const completedWorkouts = clientWorkouts?.filter(w => w?.status === 'completed');
      const totalWorkouts = clientWorkouts?.length;
      const adherenceRate = totalWorkouts > 0 ? Math.round((completedWorkouts?.length / totalWorkouts) * 100) : 0;

      // Calculate current streak
      const sortedWorkouts = [...completedWorkouts]
        ?.sort((a, b) => new Date(b?.completedAt) - new Date(a?.completedAt));
      
      let currentStreak = 0;
      let lastDate = null;
      for (const workout of sortedWorkouts) {
        const completedDate = new Date(workout?.completedAt);
        if (!lastDate) {
          currentStreak = 1;
          lastDate = completedDate;
        } else {
          const daysDiff = Math.floor((lastDate - completedDate) / (1000 * 60 * 60 * 24));
          if (daysDiff <= 2) {
            currentStreak++;
            lastDate = completedDate;
          } else {
            break;
          }
        }
      }

      const selectedClient = clients?.find(c => c?.id === clientId);
      const recentWorkouts = clientWorkouts
        ?.sort((a, b) => new Date(b?.scheduledDate) - new Date(a?.scheduledDate))
        ?.slice(0, 10)
        ?.map(w => ({
          workoutName: w?.workoutPlan?.name || 'Workout',
          status: w?.status,
          completedAt: w?.completedAt,
          scheduledDate: w?.scheduledDate,
          notes: w?.notes
        }));

      return {
        clientName: selectedClient?.client?.fullName || selectedClient?.fullName || 'Client',
        adherenceRate,
        currentStreak,
        completedWorkouts: completedWorkouts?.length,
        totalWorkouts,
        recentWorkouts,
        currentWorkoutPlan: currentWorkoutPlan ? {
          name: currentWorkoutPlan?.name,
          exercises: currentWorkoutPlan?.exercises || []
        } : null,
        fitnessGoals: selectedClient?.client?.fitnessGoals || selectedClient?.fitnessGoals || '',
        performanceTrends: adherenceRate >= 80 ? 'High adherence, consistent performance' : 
                          adherenceRate >= 60 ? 'Moderate adherence, room for improvement': 'Low adherence, needs motivation strategies'
      };
    } catch (err) {
      console.error('Error loading client performance data:', err);
      throw err;
    }
  };

  const handleAnalyze = async () => {
    if (!selectedClientId) {
      setError('Please select a client');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAnalysis(null);

    try {
      const performanceData = await loadClientPerformanceData(selectedClientId);
      setClientPerformanceData(performanceData);

      const result = await generateWorkoutAdjustments(performanceData);
      
      if (result?.success) {
        setAnalysis(result?.recommendations);
      } else {
        setError(result?.error || 'Failed to generate recommendations');
      }
    } catch (err) {
      setError(err?.message || 'An error occurred while analyzing performance data');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const formatAnalysisText = (text) => {
    if (!text) return null;

    const lines = text?.split('\n');
    return lines?.map((line, index) => {
      // Section headers (bold)
      if (line?.match(/^\d+\.\s*\*\*.*\*\*/) || line?.match(/^\*\*.*\*\*$/)) {
        const cleanLine = line?.replace(/\*\*/g, '')?.replace(/^\d+\.\s*/, '');
        return (
          <h3 key={index} className="text-lg font-bold text-primary mt-6 mb-3">
            {cleanLine}
          </h3>
        );
      }
      // Bullet points
      if (line?.trim()?.startsWith('-') || line?.trim()?.startsWith('•')) {
        return (
          <li key={index} className="ml-6 mb-2 text-muted-foreground">
            {line?.replace(/^[-•]\s*/, '')}
          </li>
        );
      }
      // Regular paragraphs
      if (line?.trim()) {
        return (
          <p key={index} className="mb-3 text-foreground leading-relaxed">
            {line}
          </p>
        );
      }
      return null;
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Sparkles" size={24} className="text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">AI Workout Recommendations</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Powered by Anthropic Claude - Personalized workout adjustments based on client performance
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Client Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-foreground mb-2">
              Select Client
            </label>
            <div className="flex gap-3">
              <Select
                value={selectedClientId}
                onChange={(e) => {
                  setSelectedClientId(e?.target?.value);
                  setAnalysis(null);
                  setError(null);
                }}
                className="flex-1"
              >
                <option value="">Choose a client...</option>
                {clients?.map(client => (
                  <option key={client?.id} value={client?.id}>
                    {client?.client?.fullName || client?.fullName || 'Unnamed Client'}
                  </option>
                ))}
              </Select>
              <Button
                onClick={handleAnalyze}
                disabled={!selectedClientId || isAnalyzing}
                className="whitespace-nowrap"
              >
                {isAnalyzing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Icon name="Sparkles" size={18} className="mr-2" />
                    Analyze Performance
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Client Performance Summary */}
          {clientPerformanceData && (
            <div className="bg-muted/30 rounded-lg p-4 mb-6 border border-border">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="User" size={16} className="text-primary" />
                Client Performance Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Adherence Rate</p>
                  <p className="text-lg font-bold text-primary">{clientPerformanceData?.adherenceRate}%</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Current Streak</p>
                  <p className="text-lg font-bold text-secondary">{clientPerformanceData?.currentStreak} days</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Completed</p>
                  <p className="text-lg font-bold text-foreground">
                    {clientPerformanceData?.completedWorkouts}/{clientPerformanceData?.totalWorkouts}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Performance</p>
                  <p className="text-sm font-medium text-foreground">
                    {clientPerformanceData?.adherenceRate >= 80 ? '🔥 Excellent' : 
                     clientPerformanceData?.adherenceRate >= 60 ? '👍 Good' : '⚠️ Needs Support'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-destructive mb-1">Analysis Error</h4>
                  <p className="text-sm text-destructive/90">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Analysis Results */}
          {analysis && (
            <div className="bg-card border border-border rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-border">
                <Icon name="Brain" size={20} className="text-primary" />
                <h3 className="text-lg font-bold text-foreground">AI-Generated Recommendations</h3>
              </div>
              <div className="prose prose-sm max-w-none">
                {formatAnalysisText(analysis)}
              </div>
            </div>
          )}

          {/* Empty State */}
          {!analysis && !error && !isAnalyzing && (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
                <Icon name="Sparkles" size={32} className="text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Ready to Generate Recommendations
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Select a client and click "Analyze Performance" to receive AI-powered workout adjustment recommendations based on their adherence patterns and progress data.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border bg-muted/20">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground flex items-center gap-2">
              <Icon name="Info" size={14} />
              Recommendations are AI-generated and should be reviewed by a qualified trainer
            </p>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIWorkoutRecommendations;